// Состояние игры на клиенте — только копия ответов сервера (ADR-0003). Кэш в localStorage с меткой времени
// нужен для «экран из кэша до ответа сервера» и офлайна с подписью времени (E06-T04, ux/11 §4 ST-OFFLINE).
import { computed, signal } from '@preact/signals';
import type { Api, ResponseOf } from '@garages/contract';
import type { Item, Player, Wallet } from '../content/types.js';
import { storage } from './storage.js';
import { setOverrides } from '../i18n/t.js';
import { coopBrief } from './coop.js';
import { mailUnclaimed } from './quests.js';
import { applyServerSettings } from './prefs.js';
import { applyTheme } from '../telegram/theme.js';

type Bootstrap = ResponseOf<Api['getBootstrap']>;
export type Districts = ResponseOf<Api['getDistricts']>;
export type Shop = ResponseOf<Api['getShop']>;
export type Inventory = ResponseOf<Api['getInventory']>;

export interface Cached<T> { data: T; at: number }

export const player = signal<Player | null>(null);
export const wallet = signal<Wallet | null>(null);
export const tutorialStep = signal<string>('done');
export const clientConfig = signal<Record<string, unknown>>({});
export const features = signal<Record<string, boolean>>({});
export const inventory = signal<Inventory | null>(null);
export const districts = signal<Cached<Districts> | null>(null);
export const shop = signal<Cached<Shop> | null>(null);
/** Возвраты звёзд, ещё не подтверждённые игроком (bootstrap.refundNotices, Р-86) — MOD-REFUND-NOTICE. */
export const refundNotices = signal<NonNullable<Bootstrap['refundNotices']>>([]);
/** Время, когда данные шапки пришли с сервера (для подписи «данные на 14:02» в офлайне). */
export const walletAt = signal<number>(0);
/** Сдвиг часов сервера: serverNow = Date.now() + offset (tech/03 §1 «Время»). */
export const serverOffset = signal(0);
export const online = signal(true);
/** Очередь «восстановилась связь» — зелёная полоса 1.5 с. */
export const backOnline = signal(false);

export const serverNow = () => Date.now() + serverOffset.value;
export const equipped = computed(() => inventory.value?.items.filter((i) => i.equipped) ?? []);

const CACHE_KEY = 'cache.v1';
interface CacheShape { player?: Player; wallet?: Wallet; at?: number; districts?: Cached<Districts>; shop?: Cached<Shop>; inventory?: Inventory; tutorialStep?: string }

export function saveCache(): void {
  const c: CacheShape = {
    ...(player.value ? { player: player.value } : {}),
    ...(wallet.value ? { wallet: wallet.value } : {}),
    at: walletAt.value,
    ...(districts.value ? { districts: districts.value } : {}),
    ...(shop.value ? { shop: shop.value } : {}),
    ...(inventory.value ? { inventory: inventory.value } : {}),
    tutorialStep: tutorialStep.value,
  };
  storage.setJson(CACHE_KEY, c);
}

/** Поднять кэш до ответа сервера. true — кэш есть, можно открыть SCR-HOME в ST-OFFLINE. */
export function loadCache(): boolean {
  const c = storage.getJson<CacheShape>(CACHE_KEY);
  if (!c?.player || !c.wallet) return false;
  player.value = c.player;
  wallet.value = c.wallet;
  walletAt.value = c.at ?? 0;
  if (c.districts) districts.value = c.districts;
  if (c.shop) shop.value = c.shop;
  if (c.inventory) inventory.value = c.inventory;
  if (c.tutorialStep) tutorialStep.value = c.tutorialStep;
  return true;
}

export function applyBootstrap(b: Bootstrap): void {
  player.value = b.player;
  wallet.value = b.wallet;
  walletAt.value = Date.now();
  tutorialStep.value = b.tutorialStep;
  clientConfig.value = b.clientConfig.values;
  // Пакет-перекрытие строк без релиза клиента (E01-T11): clientConfig.values.strings = {KEY: текст}.
  setOverrides(b.clientConfig.values['strings'] ?? {});
  features.value = b.features;
  refundNotices.value = b.refundNotices ?? [];
  mailUnclaimed.value = b.unreadMail;
  if (applyServerSettings(b.player.settings)) applyTheme(null); // настройки с другого устройства (Р-100)
  coopBrief.value = b.coop ? { coopId: b.coop.coopId, name: b.coop.name, role: b.coop.role } : null;
  serverOffset.value = Date.parse(b.serverTime) - Date.now();
  saveCache();
}

export function setWallet(w: Wallet): void {
  wallet.value = w;
  walletAt.value = Date.now();
  saveCache();
}

export function setInventoryItems(update: (items: Item[]) => Item[]): void {
  const inv = inventory.value;
  if (!inv) return;
  inventory.value = { ...inv, items: update(inv.items) };
  saveCache();
}

/** Прототип: обучение кончается на T06, шаги T07+ появятся на альфе (ux/11 §1.3). */
/** Обучение идёт: T01–T10 (альфа, E16-T03). Если сервер закрыл T07–T10 сам (autoCompleteAfter, Р-41), шаг уже 'done'. */
export function tutorialActive(step: string): boolean {
  return /^T(0[1-9]|10)$/.test(step);
}

/**
 * Перечитать профиль (GET /bootstrap): при возврате в приложение и перед боем, чтобы шапка, уровень и шаг
 * обучения не отставали от сервера (отчёт сравнения с «Тюрягой» §6 п. 1). Ошибка сети — остаёмся на кэше.
 */
export async function refreshProfile(call: (n: 'getBootstrap') => Promise<Bootstrap>): Promise<Bootstrap | null> {
  try {
    const b = await call('getBootstrap');
    applyBootstrap(b);
    return b;
  } catch { return null; }
}
